import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import InputAdornment from '@material-ui/core/InputAdornment'
import ExpansionPanel from '@material-ui/core/ExpansionPanel'
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary'
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails'
import Chip from '@material-ui/core/Chip'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SearchIcon from '@material-ui/icons/Search'
import TuneIcon from '@material-ui/icons/Tune'
import type { RefinementListExposed, RefinementListProvided } from 'react-instantsearch-core'
import {
  RefinementList,
  ClearRefinements,
  connectCurrentRefinements,
  connectRange,
  connectRefinementList,
  connectStateResults,
} from 'react-instantsearch-dom'

import {
  ProjectsAlgoliaSearchClientContext,
  ProjectsSearchStateContext,
} from '@/routes/projects/containers/search-containers/projects-search-container'
import { ProjectsFilterErrorBoundary } from './projects-filter-error-boundary'
import { ProjectsFilterSlidePanel } from './projects-filter-slide-panel'
import {
  DRAWER_CHIP_LABELS,
  DRAWER_REFINEMENT_ATTRIBUTES,
  FACET_ID_ATTRIBUTES,
  facetTokenBaseId,
  resolveFacetLabel,
  transformIdFacetItems,
  useProjectsFacetLabelLookup,
} from './projects-facet-label-lookup'

const PROJECTS_INDEX_NAME = 'Projects'

/** Numeric range in `searchState.range` (not `refinementList`). */
const BUDGET_RANGE_ATTRIBUTE = 'budgetTotal'

function itemValueToString(raw: any): string {
  if (raw == null) return ''
  if (Array.isArray(raw)) return itemValueToString(raw[0])
  return String(raw)
}

/** Stable row identity: id facets collapse legacy `id|name` vs `id\u001fname` to one row. */
function facetRowDedupeKey(attribute: string, item: any): string {
  const v = itemValueToString(item.value)
  if (FACET_ID_ATTRIBUTES.has(attribute)) {
    const base = facetTokenBaseId(v)
    return base || v
  }
  return v || String(item.label ?? '')
}

const FACET_ID_NAME_SEP = '\u001f'

function mergeFacetDuplicateRows(a: any, b: any): any {
  const count = Math.max(Number(a.count || 0), Number(b.count || 0))
  const isRefined = Boolean(a.isRefined || b.isRefined)
  let chosen = a
  if (b.isRefined && !a.isRefined) chosen = b
  else if (a.isRefined && !b.isRefined) chosen = a
  else {
    const va = itemValueToString(a.value)
    const vb = itemValueToString(b.value)
    if (va.includes(FACET_ID_NAME_SEP) && !vb.includes(FACET_ID_NAME_SEP)) chosen = a
    else if (vb.includes(FACET_ID_NAME_SEP) && !va.includes(FACET_ID_NAME_SEP)) chosen = b
  }
  return { ...chosen, count, isRefined }
}

/** Algolia SFFV / mixed token encodings can return duplicate facet hits — one checkbox per logical value. */
function dedupeFacetDisplayItems(attribute: string, items: any[]): any[] {
  const map = new Map<string, any>()
  for (const item of items || []) {
    const key = facetRowDedupeKey(attribute, item)
    if (!key) continue
    const cur = map.get(key)
    if (!cur) {
      map.set(key, item)
      continue
    }
    map.set(key, mergeFacetDuplicateRows(cur, item))
  }
  return Array.from(map.values())
}

/** Fingerprint of applied drawer facets + budget range (stable sort) for draft sync while panel is open. */
function appliedDrawerAndBudgetFingerprint(searchState: any): string {
  const rl = searchState?.refinementList || {}
  const parts: string[] = []
  DRAWER_REFINEMENT_ATTRIBUTES.forEach((attr) => {
    const v = rl[attr]
    let arr: string[] = []
    if (v != null && v !== '') {
      arr = (Array.isArray(v) ? v : [v])
        .flatMap((x: any) => (Array.isArray(x) ? x : [x]))
        .map((x) => String(x))
        .filter(Boolean)
    }
    arr.sort()
    parts.push(`${attr}:${arr.join('\u0002')}`)
  })
  const br = searchState?.range?.[BUDGET_RANGE_ATTRIBUTE]
  let b = ''
  if (br && typeof br === 'object') {
    const mn = br.min != null && br.min !== '' ? String(br.min) : ''
    const mx = br.max != null && br.max !== '' ? String(br.max) : ''
    b = `${mn}\u0003${mx}`
  }
  return parts.join('\u0004') + `\u0005${b}`
}

function transformCurrentRefinementItems(items: any[], lookup: ReturnType<typeof useProjectsFacetLabelLookup>): any[] {
  return (items || []).map((row: any) => {
    if (Array.isArray(row.items) && row.items.length) {
      return {
        ...row,
        items: row.items.map((nest: any) => ({
          ...nest,
          label: resolveFacetLabel(String(row.attribute ?? ''), String(nest.label ?? nest.value ?? ''), lookup),
        })),
      }
    }
    return {
      ...row,
      label: resolveFacetLabel(String(row.attribute ?? ''), String(row.label ?? row.value ?? ''), lookup),
    }
  })
}

function normalizeDrawerDraftFromSearchState(searchState: any): Record<string, string[]> {
  const rl = searchState?.refinementList || {}
  const pick: Record<string, string[]> = {}
  DRAWER_REFINEMENT_ATTRIBUTES.forEach((attr) => {
    const v = rl[attr]
    if (v != null && v !== '') {
      const arr = (Array.isArray(v) ? v : [v]) as any[]
      pick[attr] = arr
        .flatMap((x) => (Array.isArray(x) ? x : [x]))
        .map((x) => String(x))
        .filter(Boolean)
    }
  })
  return pick
}

function mergeDrawerDraftIntoRefinementList(searchState: any, draft: Record<string, string[]>) {
  const base: Record<string, any> = { ...(searchState?.refinementList || {}) }
  DRAWER_REFINEMENT_ATTRIBUTES.forEach((attr) => {
    const d = draft[attr]
    if (d && d.length) base[attr] = [...d]
    else base[attr] = ''
  })
  return base
}

function drawerDraftEqualsApplied(draft: Record<string, string[]>, refinementList: any) {
  const rl = refinementList || {}
  for (const attr of DRAWER_REFINEMENT_ATTRIBUTES) {
    const a = [...(draft[attr] || [])].map(String).sort().join('\0')
    const raw = rl[attr]
    const bArr = raw == null || raw === '' ? [] : Array.isArray(raw) ? raw : [raw]
    const b = bArr
      .flatMap((x: any) => (Array.isArray(x) ? x : [x]))
      .map(String)
      .sort()
      .join('\0')
    if (a !== b) return false
  }
  return true
}

function refinementListToFacetFilters(rl: Record<string, any>): string[][] | undefined {
  const groups: string[][] = []
  Object.entries(rl).forEach(([attr, raw]) => {
    if (raw == null || raw === '') return
    const vals = Array.isArray(raw) ? raw : [raw]
    const parts = vals
      .map((v: any) => {
        const s = Array.isArray(v) ? v[0] : v
        if (s == null || s === '') return null
        return `${attr}:${String(s)}`
      })
      .filter(Boolean) as string[]
    if (parts.length) groups.push(parts)
  })
  return groups.length ? groups : undefined
}

function parseBudgetInput(raw: string): number | undefined {
  const t = String(raw || '')
    .trim()
    .replace(/[$€£¥]/g, '')
    .replace(/,/g, '')
  if (!t) return undefined
  const n = Number(t)
  return Number.isFinite(n) ? n : undefined
}

function buildPreviewSearchParams(
  searchState: any,
  refinementList: Record<string, any>,
  budgetRange?: { min?: number; max?: number }
) {
  const cfg = searchState?.configure || {}
  const facetFilters = refinementListToFacetFilters(refinementList)
  const params: Record<string, any> = {
    hitsPerPage: 0,
    query: typeof cfg.query === 'string' ? cfg.query : '',
  }
  if (cfg.filters) params.filters = cfg.filters
  if (facetFilters) params.facetFilters = facetFilters
  const br = budgetRange ?? searchState?.range?.[BUDGET_RANGE_ATTRIBUTE]
  const numericFilters: string[] = []
  if (br && typeof br === 'object') {
    if (br.min != null && Number.isFinite(Number(br.min))) {
      numericFilters.push(`${BUDGET_RANGE_ATTRIBUTE}>=${Number(br.min)}`)
    }
    if (br.max != null && Number.isFinite(Number(br.max))) {
      numericFilters.push(`${BUDGET_RANGE_ATTRIBUTE}<=${Number(br.max)}`)
    }
  }
  if (numericFilters.length) params.numericFilters = numericFilters
  return params
}

function budgetDraftStringsMatchApplied(
  minStr: string,
  maxStr: string,
  searchState: any
): boolean {
  const applied = searchState?.range?.[BUDGET_RANGE_ATTRIBUTE]
  const aMin = applied?.min
  const aMax = applied?.max
  const dMin = minStr.trim() === '' ? undefined : parseBudgetInput(minStr)
  const dMax = maxStr.trim() === '' ? undefined : parseBudgetInput(maxStr)
  const norm = (x: any) => (x == null || x === '' ? undefined : Number(x))
  return norm(aMin) === dMin && norm(aMax) === dMax
}

function mergeDraftBudgetRangeIntoSearchState(prev: any, budgetMinStr: string, budgetMaxStr: string) {
  const dMin = budgetMinStr.trim() === '' ? undefined : parseBudgetInput(budgetMinStr)
  const dMax = budgetMaxStr.trim() === '' ? undefined : parseBudgetInput(budgetMaxStr)
  const nextRange: Record<string, any> = { ...(prev.range || {}) }
  if (dMin != null || dMax != null) {
    const o: Record<string, number> = {}
    if (dMin != null) o.min = dMin
    if (dMax != null) o.max = dMax
    nextRange[BUDGET_RANGE_ATTRIBUTE] = o
  } else {
    delete nextRange[BUDGET_RANGE_ATTRIBUTE]
  }
  const keys = Object.keys(nextRange)
  return keys.length ? nextRange : undefined
}

function previewBudgetRangeFromDraft(
  budgetMinStr: string,
  budgetMaxStr: string
): { min?: number; max?: number } | undefined {
  const min = budgetMinStr.trim() === '' ? undefined : parseBudgetInput(budgetMinStr)
  const max = budgetMaxStr.trim() === '' ? undefined : parseBudgetInput(budgetMaxStr)
  const out: { min?: number; max?: number } = {}
  if (min != null) out.min = min
  if (max != null) out.max = max
  return Object.keys(out).length ? out : undefined
}

type ProjectsFilterDraftContextValue = {
  deferDrawerRefinements: boolean
  drawerOpen: boolean
  draftRefinementList: Record<string, string[]>
  budgetMinStr: string
  budgetMaxStr: string
  setBudgetMinStr: (v: string) => void
  setBudgetMaxStr: (v: string) => void
  toggleDraft: (attribute: string, value: string) => void
  removeDraftValue: (attribute: string, value: string) => void
  resetDraft: () => void
  isDraftSelected: (attribute: string, value: string) => boolean
  draftSelectionSummary: (attribute: string, items: any[]) => string
  previewNbHits: number | null
  previewLoading: boolean
  commitAndClose: () => void
}

const ProjectsFilterDraftContext = createContext<ProjectsFilterDraftContextValue | null>(null)

const useStyles = makeStyles(() => ({
  /** Matches `ProjectsHitsGrid` grid item horizontal margin (24px) so toolbar aligns with cards. */
  toolbarInset: {
    paddingLeft: 24,
    paddingRight: 24,
    boxSizing: 'border-box',
    width: '100%',
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '12px 0 0',
    flexWrap: 'wrap',
  },
  /** Filters control: outline; icon + label + optional count badge. */
  filtersButton: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: 'transparent',
    color: '#1c1c1f',
    padding: '6px 14px 6px 12px',
    boxShadow: 'none',
    border: '1px solid #cfd1db',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
      boxShadow: 'none',
      border: '1px solid #b8bcc8',
    },
  },
  filtersButtonLeading: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  filtersButtonIcon: {
    fontSize: 20,
    color: '#3d3d45',
    display: 'block',
    flexShrink: 0,
  },
  filtersButtonLabel: {
    display: 'block',
    lineHeight: '20px',
    fontSize: 14,
    fontWeight: 600,
    paddingTop: 1,
  },
  filtersButtonBadge: {
    minWidth: 22,
    height: 22,
    padding: '0 7px',
    borderRadius: 11,
    backgroundColor: '#000',
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '22px',
    textAlign: 'center',
    boxSizing: 'border-box',
    marginLeft: 2,
  },
  /** One row: Filters button then refinement pills (screenshot layout). */
  filterBarRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    margin: '10px 0 16px',
    minHeight: 0,
    width: '100%',
  },
  filterPillsGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  filterPill: {
    backgroundColor: '#ececf2',
    color: '#1c1c1f',
    height: 30,
    borderRadius: 15,
    fontWeight: 500,
    fontSize: 13,
    '&:focus': {
      backgroundColor: '#e2e3ea',
    },
  },
  filterPillLabel: {
    paddingLeft: 12,
    paddingRight: 6,
  },
  filterPillDelete: {
    color: '#5c5c66',
    fontSize: 18,
    marginRight: 4,
    '&:hover': {
      color: '#1c1c1f',
    },
  },
  /** Sits on the same row as Filters + pills; pinned to the right. */
  clearLink: {
    marginLeft: 'auto',
    marginRight: 20,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    border: 0,
    background: 'transparent',
    color: '#6d6f87',
    cursor: 'pointer',
    '& .ais-ClearRefinements-button': {
      border: 0,
      background: 'transparent',
      color: 'inherit',
      cursor: 'pointer',
      padding: 0,
      font: 'inherit',
      textDecoration: 'underline',
      textUnderlineOffset: 2,
      '&:hover': {
        textDecoration: 'underline',
        color: '#1c1c1f',
      },
      '&:disabled': {
        textDecoration: 'underline',
        opacity: 0.5,
      },
    },
  },
  drawerContent: {
    width: '100%',
    paddingBottom: 8,
  },
  accRoot: {
    boxShadow: 'none',
    borderBottom: '1px solid #ececf2',
    margin: 0,
    '&:before': {
      display: 'none',
    },
    '&:first-child': {
      borderTop: '1px solid #ececf2',
    },
  },
  accSummary: {
    minHeight: 52,
    paddingLeft: 16,
    paddingRight: 10,
    '& .MuiExpansionPanelSummary-expandIcon': {
      padding: 8,
      marginRight: 8,
    },
    '& .MuiExpansionPanelSummary-content': {
      margin: '12px 0',
      alignItems: 'center',
      width: '100%',
      overflow: 'hidden',
    },
  },
  accSummaryInner: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 8,
    minWidth: 0,
  },
  accTitle: {
    fontWeight: 600,
    fontSize: 16,
    flexShrink: 0,
  },
  accSelection: {
    flex: 1,
    minWidth: 0,
    textAlign: 'right',
    fontSize: 13,
    color: '#6d6f87',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  accDetails: {
    display: 'block',
    padding: '4px 28px 16px 24px',
  },
  facetSearchField: {
    width: '100%',
    marginBottom: 8,
    '& .MuiOutlinedInput-input': {
      paddingTop: 10,
      paddingBottom: 10,
      fontSize: 14,
    },
  },
  facetRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 12,
    padding: '6px 0',
    boxSizing: 'border-box',
  },
  facetCheckbox: {
    width: 18,
    height: 18,
    flexShrink: 0,
    accentColor: '#000',
    cursor: 'pointer',
  },
  facetLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
    cursor: 'pointer',
    margin: 0,
  },
  facetLabelText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  facetCount: {
    flexShrink: 0,
    color: '#6d6f87',
    fontSize: 13,
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
    minWidth: 48,
  },
  facetEmpty: {
    fontSize: 13,
    color: '#9e9e9e',
    padding: '4px 0 8px',
  },
  budgetRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingTop: 4,
  },
  budgetDash: {
    color: '#6d6f87',
    flexShrink: 0,
  },
  budgetField: {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-input': {
      paddingTop: 10,
      paddingBottom: 10,
      fontSize: 14,
    },
  },
  shootSubLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    color: '#6d6f87',
    marginTop: 12,
    marginBottom: 6,
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  facetPlainSection: {
    marginBottom: 4,
  },
  toolbarRefinementList: {
    width: '100%',
    display: 'block',
    '& ul': {
      display: 'block',
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    '& li': {
      display: 'block',
      padding: '4px 0',
    },
    '& label': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      gap: 12,
      cursor: 'pointer',
    },
    '& input[type="checkbox"]': {
      accentColor: '#000',
      width: 18,
      height: 18,
    },
    '& .ais-RefinementList-labelText': {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .ais-RefinementList-count': {
      flexShrink: 0,
      textAlign: 'right',
      minWidth: 40,
      marginRight: 4,
      color: '#6d6f87',
      fontVariantNumeric: 'tabular-nums',
    },
  },
  drawerFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 20px',
    borderTop: '1px solid #ececf2',
    background: '#fff',
    flexShrink: 0,
  },
  resetButton: {
    minWidth: 86,
    textTransform: 'none',
  },
  applyButton: {
    flex: 1,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 10,
    backgroundColor: '#000',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#222',
    },
  },
  panelLoading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
  },
}))

const chipClasses = (classes: ReturnType<typeof useStyles>) => ({
  root: classes.filterPill,
  label: classes.filterPillLabel,
  deleteIcon: classes.filterPillDelete,
})

const ProjectsRefinementChips = connectCurrentRefinements(function ProjectsRefinementChipsInner({
  items,
  refine,
  onOpenDrawer,
}: {
  items: any[]
  refine: (v: any) => void
  onOpenDrawer?: () => void
}) {
  const classes = useStyles()
  const lookup = useProjectsFacetLabelLookup()
  const ctx = useContext(ProjectsFilterDraftContext)
  const drawerOpen = Boolean(ctx?.drawerOpen)
  const draftList = ctx?.draftRefinementList ?? {}
  const removeDraft = ctx?.removeDraftValue
  const drawerAttrSet = new Set<string>([
    ...(DRAWER_REFINEMENT_ATTRIBUTES as unknown as string[]),
    BUDGET_RANGE_ATTRIBUTE,
  ])

  const chips: React.ReactNode[] = []
  const cc = chipClasses(classes)

  // Applied refinements from InstantSearch (toolbar, and drawer facets when panel is closed).
  // While the drawer is open, drawer facet rows are omitted from `items` until applied — pills for those come from draft below.
  ;(items || []).forEach((item: any) => {
    if (!item) return
    if (drawerOpen && item.attribute && drawerAttrSet.has(item.attribute)) {
      return
    }
    if (Array.isArray(item.items) && item.items.length > 0) {
      item.items.forEach((nest: any) => {
        if (!nest) return
        chips.push(
          <Chip
            key={`${String(item.label)}-${String(nest.label)}`}
            label={nest.label}
            size="small"
            classes={cc}
            onDelete={() => refine(nest.value)}
          />
        )
      })
    } else {
      chips.push(
        <Chip
          key={String(item.label)}
          label={item.label}
          size="small"
          classes={cc}
          onDelete={() => refine(item.value)}
        />
      )
    }
  })

  if (drawerOpen && ctx) {
    const bm = ctx.budgetMinStr.trim()
    const bx = ctx.budgetMaxStr.trim()
    if (bm || bx) {
      const fmt = (s: string) => {
        const n = parseBudgetInput(s)
        return n != null ? `$${n.toLocaleString()}` : s
      }
      let bl = 'Budget: '
      if (bm && bx) bl += `${fmt(bm)} – ${fmt(bx)}`
      else if (bm) bl += `≥ ${fmt(bm)}`
      else bl += `≤ ${fmt(bx)}`
      chips.push(
        <Chip
          key="draft-budget"
          label={bl}
          size="small"
          classes={cc}
          onDelete={() => {
            ctx.setBudgetMinStr('')
            ctx.setBudgetMaxStr('')
          }}
        />
      )
    }
    DRAWER_REFINEMENT_ATTRIBUTES.forEach((attr) => {
      const vals = draftList[attr] || []
      const group = DRAWER_CHIP_LABELS[attr] || attr
      vals.forEach((val) => {
        chips.push(
          <Chip
            key={`draft-${attr}-${val}`}
            label={`${group}: ${resolveFacetLabel(attr, val, lookup)}`}
            size="small"
            classes={cc}
            onDelete={() => removeDraft?.(attr, val)}
          />
        )
      })
    })
  }

  const pillCount = chips.length

  return (
    <div className={classes.filterBarRow}>
      <Button
        type="button"
        variant="text"
        className={classes.filtersButton}
        onClick={() => onOpenDrawer?.()}
      >
        <span className={classes.filtersButtonLeading}>
          <TuneIcon className={classes.filtersButtonIcon} aria-hidden />
          <span className={classes.filtersButtonLabel}>Filters</span>
        </span>
        {pillCount > 0 ? <span className={classes.filtersButtonBadge}>{pillCount}</span> : null}
      </Button>
      <div className={classes.filterPillsGroup}>{chips}</div>
      <ClearRefinements
        translations={{ reset: 'Clear all' }}
        className={classes.clearLink}
        clearsQuery={false}
      />
    </div>
  )
}) as unknown as React.ComponentType<{
  transformItems?: (items: any[]) => any[]
  onOpenDrawer: () => void
}>

function ProjectsRefinementChipsBridge({ onOpenDrawer }: { onOpenDrawer: () => void }) {
  const lookup = useProjectsFacetLabelLookup()
  return (
    <ProjectsRefinementChips
      transformItems={(items) => transformCurrentRefinementItems(items, lookup)}
      onOpenDrawer={onOpenDrawer}
    />
  )
}

const ProjectsFilterDraftProviderInner = connectStateResults(function ProjectsFilterDraftProviderInnerBase({
  searchState,
  searchResults,
  drawerOpen,
  onCloseDrawer,
  children,
}: any) {
  const lookup = useProjectsFacetLabelLookup()
  const searchClient = useContext(ProjectsAlgoliaSearchClientContext)
  const setSearchState = useContext(ProjectsSearchStateContext)
  const [draftRefinementList, setDraftRefinementList] = useState<Record<string, string[]>>({})
  const [budgetMinStr, setBudgetMinStr] = useState('')
  const [budgetMaxStr, setBudgetMaxStr] = useState('')
  const [previewNbHits, setPreviewNbHits] = useState<number | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const prevDrawerOpenRef = useRef(false)
  /** Last applied drawer+budget fingerprint — when it changes while the drawer is open, resync draft (chips / Clear all). */
  const prevDrawerAppliedFingerprintRef = useRef<string | null>(null)

  useEffect(() => {
    if (!drawerOpen) {
      prevDrawerOpenRef.current = false
      prevDrawerAppliedFingerprintRef.current = null
      return
    }

    const slice = appliedDrawerAndBudgetFingerprint(searchState)
    const opening = !prevDrawerOpenRef.current
    const appliedChanged = prevDrawerAppliedFingerprintRef.current !== slice

    if (opening || appliedChanged) {
      setDraftRefinementList(normalizeDrawerDraftFromSearchState(searchState))
      const br = searchState?.range?.[BUDGET_RANGE_ATTRIBUTE]
      setBudgetMinStr(br?.min != null && br.min !== '' ? String(br.min) : '')
      setBudgetMaxStr(br?.max != null && br.max !== '' ? String(br.max) : '')
      prevDrawerAppliedFingerprintRef.current = slice
    }
    prevDrawerOpenRef.current = true
  }, [drawerOpen, searchState])

  const mergedRefinementList = useMemo(
    () => mergeDrawerDraftIntoRefinementList(searchState, draftRefinementList),
    [searchState, draftRefinementList]
  )

  const previewBudgetRange = useMemo(
    () => previewBudgetRangeFromDraft(budgetMinStr, budgetMaxStr),
    [budgetMinStr, budgetMaxStr]
  )

  const matchesApplied = useMemo(
    () =>
      drawerDraftEqualsApplied(draftRefinementList, searchState?.refinementList) &&
      budgetDraftStringsMatchApplied(budgetMinStr, budgetMaxStr, searchState),
    [draftRefinementList, searchState?.refinementList, budgetMinStr, budgetMaxStr, searchState]
  )

  useEffect(() => {
    if (!drawerOpen || !searchClient) {
      setPreviewNbHits(null)
      setPreviewLoading(false)
      return
    }
    if (matchesApplied) {
      setPreviewNbHits(searchResults?.nbHits ?? null)
      setPreviewLoading(false)
      return
    }
    let cancelled = false
    const t = window.setTimeout(() => {
      ;(async () => {
        setPreviewLoading(true)
        try {
          const params = buildPreviewSearchParams(
            searchState,
            mergedRefinementList,
            previewBudgetRange
          )
          const res = await searchClient.search([{ indexName: PROJECTS_INDEX_NAME, params }])
          const first = res?.results?.[0] as { nbHits?: number } | undefined
          const nb = first?.nbHits
          if (!cancelled) setPreviewNbHits(typeof nb === 'number' ? nb : null)
        } catch {
          if (!cancelled) setPreviewNbHits(null)
        } finally {
          if (!cancelled) setPreviewLoading(false)
        }
      })()
    }, 280)
    return () => {
      cancelled = true
      window.clearTimeout(t)
    }
  }, [
    drawerOpen,
    searchClient,
    searchState,
    mergedRefinementList,
    matchesApplied,
    searchResults,
    previewBudgetRange,
  ])

  const toggleDraft = useCallback((attribute: string, value: string) => {
    if (!DRAWER_REFINEMENT_ATTRIBUTES.includes(attribute as any)) return
    setDraftRefinementList((prev) => {
      const cur = [...(prev[attribute] || [])]
      const valStr = String(value)
      const i = cur.findIndex(
        (t) => String(t) === valStr || facetTokenBaseId(String(t)) === facetTokenBaseId(valStr)
      )
      if (i >= 0) cur.splice(i, 1)
      else cur.push(valStr)
      const next = { ...prev }
      if (cur.length) next[attribute] = cur
      else delete next[attribute]
      return next
    })
  }, [])

  const removeDraftValue = useCallback((attribute: string, value: string) => {
    if (!DRAWER_REFINEMENT_ATTRIBUTES.includes(attribute as any)) return
    setDraftRefinementList((prev) => {
      const valStr = String(value)
      const cur = [...(prev[attribute] || [])].filter(
        (x) => String(x) !== valStr && facetTokenBaseId(String(x)) !== facetTokenBaseId(valStr)
      )
      const next = { ...prev }
      if (cur.length) next[attribute] = cur
      else delete next[attribute]
      return next
    })
  }, [])

  const resetDraft = useCallback(() => {
    setDraftRefinementList({})
    setBudgetMinStr('')
    setBudgetMaxStr('')
  }, [])

  const isDraftSelected = useCallback(
    (attribute: string, value: string) =>
      (draftRefinementList[attribute] || []).some(
        (t) =>
          String(t) === String(value) || facetTokenBaseId(String(t)) === facetTokenBaseId(String(value))
      ),
    [draftRefinementList]
  )

  const draftSelectionSummary = useCallback(
    (attribute: string, items: any[]) => {
      const selected = draftRefinementList[attribute] || []
      if (!selected.length) return ''
      const labels = selected.map((val) => {
        const valStr = String(val)
        const hit = items.find((it: any) => {
          const iv = itemValueToString(it.value)
          return iv === valStr || facetTokenBaseId(iv) === facetTokenBaseId(valStr)
        })
        const raw = hit ? String(hit.label) : valStr
        return resolveFacetLabel(attribute, raw, lookup)
      })
      return labels.join(', ')
    },
    [draftRefinementList, lookup]
  )

  const commitAndClose = useCallback(() => {
    if (typeof setSearchState === 'function') {
      setSearchState((prev: any) => {
        const mergedRL = mergeDrawerDraftIntoRefinementList(prev, draftRefinementList)
        const newRange = mergeDraftBudgetRangeIntoSearchState(prev, budgetMinStr, budgetMaxStr)
        if (newRange !== undefined) {
          return { ...prev, page: 1, refinementList: { ...mergedRL }, range: newRange }
        }
        const { range: _drop, ...prevWithoutRange } = prev
        return { ...prevWithoutRange, page: 1, refinementList: { ...mergedRL } }
      })
    }
    onCloseDrawer()
  }, [setSearchState, draftRefinementList, budgetMinStr, budgetMaxStr, onCloseDrawer])

  const ctxValue = useMemo(
    (): ProjectsFilterDraftContextValue => ({
      deferDrawerRefinements: true,
      drawerOpen,
      draftRefinementList,
      budgetMinStr,
      budgetMaxStr,
      setBudgetMinStr,
      setBudgetMaxStr,
      toggleDraft,
      removeDraftValue,
      resetDraft,
      isDraftSelected,
      draftSelectionSummary,
      previewNbHits,
      previewLoading,
      commitAndClose,
    }),
    [
      drawerOpen,
      draftRefinementList,
      budgetMinStr,
      budgetMaxStr,
      toggleDraft,
      removeDraftValue,
      resetDraft,
      isDraftSelected,
      draftSelectionSummary,
      previewNbHits,
      previewLoading,
      commitAndClose,
    ]
  )

  return <ProjectsFilterDraftContext.Provider value={ctxValue}>{children}</ProjectsFilterDraftContext.Provider>
}) as React.ComponentType<any>

function ProjectsFilterDraftProvider({
  drawerOpen,
  onCloseDrawer,
  children,
}: {
  drawerOpen: boolean
  onCloseDrawer: () => void
  children: React.ReactNode
}) {
  return (
    <ProjectsFilterDraftProviderInner drawerOpen={drawerOpen} onCloseDrawer={onCloseDrawer}>
      {children}
    </ProjectsFilterDraftProviderInner>
  )
}

function ProjectsFilterDrawerFooter({ facetsReady }: { facetsReady: boolean }) {
  const classes = useStyles()
  const ctx = useContext(ProjectsFilterDraftContext)
  if (!ctx) return null
  const { previewNbHits, previewLoading, commitAndClose, resetDraft } = ctx
  const showCount = facetsReady && !previewLoading && previewNbHits != null
  return (
    <div className={classes.drawerFooter}>
      <Button variant="outlined" className={classes.resetButton} onClick={resetDraft} disabled={!facetsReady}>
        Reset
      </Button>
      <Button
        variant="contained"
        color="default"
        className={classes.applyButton}
        onClick={commitAndClose}
        disabled={!facetsReady || previewLoading}
      >
        {showCount ? `Show ${Number(previewNbHits).toLocaleString()} projects` : 'Show projects'}
      </Button>
    </div>
  )
}

/** Registers `range` metadata for chips without rendering UI (drawer uses draft min/max fields). */
const ProjectsHiddenBudgetRange = connectRange(() => null) as unknown as React.ComponentType<{
  attribute: string
}>

function EstimatedBudgetAccordion() {
  const classes = useStyles()
  const ctx = useContext(ProjectsFilterDraftContext)
  const summary = useMemo(() => {
    if (!ctx) return ''
    const a = ctx.budgetMinStr.trim()
    const b = ctx.budgetMaxStr.trim()
    if (!a && !b) return ''
    const fmt = (s: string) => {
      const n = parseBudgetInput(s)
      return n != null ? `$${n.toLocaleString()}` : s
    }
    if (a && b) return `${fmt(a)} – ${fmt(b)}`
    if (a) return `≥ ${fmt(a)}`
    return `≤ ${fmt(b)}`
  }, [ctx?.budgetMinStr, ctx?.budgetMaxStr])
  if (!ctx) return null
  return (
    <ExpansionPanel
      className={classes.accRoot}
      square
      elevation={0}
      defaultExpanded={false}
      TransitionProps={{ timeout: 'auto', unmountOnExit: false } as any}
    >
      <ExpansionPanelSummary
        className={classes.accSummary}
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        aria-controls="facet-budget-content"
        id="facet-budget-header"
      >
        <div className={classes.accSummaryInner}>
          <Typography component="span" className={classes.accTitle}>
            Estimated budget
          </Typography>
          <Typography component="span" className={classes.accSelection} title={summary || undefined}>
            {summary || 'Any'}
          </Typography>
        </div>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.accDetails} id="facet-budget-content">
        <div className={classes.budgetRow}>
          <TextField
            className={classes.budgetField}
            size="small"
            variant="outlined"
            placeholder="Min $"
            value={ctx.budgetMinStr}
            onChange={(e) => ctx.setBudgetMinStr(e.target.value)}
            inputProps={{ 'aria-label': 'Minimum budget', inputMode: 'decimal' }}
          />
          <span className={classes.budgetDash}>–</span>
          <TextField
            className={classes.budgetField}
            size="small"
            variant="outlined"
            placeholder="Max $"
            value={ctx.budgetMaxStr}
            onChange={(e) => ctx.setBudgetMaxStr(e.target.value)}
            inputProps={{ 'aria-label': 'Maximum budget', inputMode: 'decimal' }}
          />
        </div>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  )
}

type FacetAccordionOwnProps = {
  panelId: string
  title: string
  classes: ReturnType<typeof useStyles>
  /** When true, always render facet search UI (Algolia SFFV still requires `searchable` on the widget). */
  facetSearchEnabled?: boolean
  facetSearchPlaceholder?: string
  /** List + optional search only; no accordion (e.g. CITY / TYPE inside Shoot details). */
  plain?: boolean
}

type FacetAccordionInnerProps = RefinementListProvided &
  FacetAccordionOwnProps &
  Pick<RefinementListExposed, 'attribute'> & { searchable?: boolean }

const FacetAccordionInner: React.FunctionComponent<FacetAccordionInnerProps> = ({
  panelId,
  title,
  attribute,
  classes,
  items,
  refine,
  canRefine,
  facetSearchEnabled = false,
  facetSearchPlaceholder = 'Search',
  plain = false,
}) => {
  const lookup = useProjectsFacetLabelLookup()
  const filterDraft = useContext(ProjectsFilterDraftContext)
  const useDraft =
    Boolean(filterDraft?.deferDrawerRefinements) &&
    DRAWER_REFINEMENT_ATTRIBUTES.includes(attribute as (typeof DRAWER_REFINEMENT_ATTRIBUTES)[number])

  const [facetQuery, setFacetQuery] = useState('')

  const showFacetSearch = facetSearchEnabled

  const listItems = useMemo(() => dedupeFacetDisplayItems(attribute, items || []), [attribute, items])

  const selectionSummary = useMemo(() => {
    if (useDraft && filterDraft) {
      return filterDraft.draftSelectionSummary(attribute, listItems)
    }
    const labels = listItems
      .filter((item: any) => item && item.isRefined)
      .map((item: any) =>
        resolveFacetLabel(attribute, String(item.label ?? item.value ?? ''), lookup)
      )
    return labels.length ? labels.join(', ') : ''
  }, [listItems, useDraft, filterDraft, attribute, lookup])

  const displayItems = useMemo(() => {
    const resolved = listItems.map((item: any) => ({
      ...item,
      label: resolveFacetLabel(attribute, String(item.label ?? item.value ?? ''), lookup),
    }))
    if (!facetSearchEnabled || !facetQuery.trim()) {
      return resolved
    }
    const q = facetQuery.trim().toLowerCase()
    return resolved.filter((item: any) => String(item.label).toLowerCase().includes(q))
  }, [listItems, facetQuery, facetSearchEnabled, attribute, lookup])

  const emptyMessage = (() => {
    if (displayItems.length > 0) return null
    if (facetSearchEnabled && facetQuery.trim()) return 'No matches'
    if (!canRefine) return 'No values'
    return 'No values'
  })()

  const listBody = (
    <>
      {showFacetSearch ? (
        <TextField
          className={classes.facetSearchField}
          size="small"
          fullWidth
          variant="outlined"
          placeholder={facetSearchPlaceholder}
          value={facetQuery}
          onChange={(e) => {
            const q = e.target.value
            setFacetQuery(q)
          }}
          inputProps={{ 'aria-label': facetSearchPlaceholder }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon style={{ color: '#9e9e9e', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
      ) : null}
      {emptyMessage != null ? (
        <Typography variant="body2" className={classes.facetEmpty}>
          {emptyMessage}
        </Typography>
      ) : (
        displayItems.map((item: any) => {
          const v = itemValueToString(item.value)
          const rowKey = facetRowDedupeKey(attribute, item) || v || String(item.label ?? '')
          const checked = useDraft && filterDraft ? filterDraft.isDraftSelected(attribute, v) : Boolean(item.isRefined)
          const rowLabel = String(item.label ?? item.value ?? '')
          return (
            <div key={`${attribute}-${rowKey}`} className={classes.facetRow}>
              <label className={classes.facetLabel}>
                <input
                  type="checkbox"
                  className={classes.facetCheckbox}
                  checked={checked}
                  onChange={() =>
                    useDraft && filterDraft
                      ? filterDraft.toggleDraft(attribute, v)
                      : refine(item.value)
                  }
                />
                <span className={classes.facetLabelText}>{rowLabel}</span>
              </label>
              <span className={classes.facetCount}>{Number(item.count || 0).toLocaleString()}</span>
            </div>
          )
        })
      )}
    </>
  )

  if (plain) {
    return (
      <div className={classes.facetPlainSection} id={`${panelId}-plain`}>
        {listBody}
      </div>
    )
  }

  return (
    <ExpansionPanel
      className={classes.accRoot}
      square
      elevation={0}
      defaultExpanded={false}
      TransitionProps={{ timeout: 'auto', unmountOnExit: false } as any}
    >
      <ExpansionPanelSummary
        className={classes.accSummary}
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        aria-controls={`${panelId}-content`}
        id={`${panelId}-header`}
      >
        <div className={classes.accSummaryInner}>
          <Typography component="span" className={classes.accTitle}>
            {title}
          </Typography>
          <Typography component="span" className={classes.accSelection} title={selectionSummary || undefined}>
            {selectionSummary}
          </Typography>
        </div>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.accDetails} id={`${panelId}-content`}>
        {listBody}
      </ExpansionPanelDetails>
    </ExpansionPanel>
  )
}

type FacetAccordionSectionProps = RefinementListExposed & FacetAccordionOwnProps

const FacetAccordionSection = connectRefinementList(FacetAccordionInner as any) as unknown as React.ComponentType<
  FacetAccordionSectionProps
>

function ProjectTeamAccordion({
  classes,
  makeTransformItems,
}: {
  classes: ReturnType<typeof useStyles>
  makeTransformItems: (attribute: string) => (items: any[]) => any[]
}) {
  const lookup = useProjectsFacetLabelLookup()
  const filterDraft = useContext(ProjectsFilterDraftContext)

  const combined = useMemo(() => {
    if (!filterDraft) return 'Any'
    const contacts = filterDraft.draftRefinementList.projectTeamContactIds || []
    const roles = filterDraft.draftRefinementList.projectTeamRoleIds || []
    const parts = [
      ...contacts.map((id) => resolveFacetLabel('projectTeamContactIds', id, lookup)),
      ...roles.map((id) => resolveFacetLabel('projectTeamRoleIds', id, lookup)),
    ].filter(Boolean)
    return parts.length ? parts.join(', ') : 'Any'
  }, [filterDraft?.draftRefinementList, lookup])

  return (
    <ExpansionPanel
      className={classes.accRoot}
      square
      elevation={0}
      defaultExpanded={false}
      TransitionProps={{ timeout: 'auto', unmountOnExit: false } as any}
    >
      <ExpansionPanelSummary
        className={classes.accSummary}
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        aria-controls="facet-project-team-content"
        id="facet-project-team-header"
      >
        <div className={classes.accSummaryInner}>
          <Typography component="span" className={classes.accTitle}>
            Project team
          </Typography>
          <Typography component="span" className={classes.accSelection} title={combined || undefined}>
            {combined}
          </Typography>
        </div>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.accDetails} id="facet-project-team-content">
        <Typography component="div" className={classes.shootSubLabel}>
          CONTACT
        </Typography>
        <FacetAccordionSection
          panelId="facet-project-team-contact"
          title=""
          attribute="projectTeamContactIds"
          facetOrdering={false}
          searchable={false}
          facetSearchEnabled
          limit={100}
          transformItems={makeTransformItems('projectTeamContactIds')}
          classes={classes}
          facetSearchPlaceholder="Search members..."
          plain
        />
        <Typography component="div" className={classes.shootSubLabel}>
          ROLE
        </Typography>
        <FacetAccordionSection
          panelId="facet-project-team-role"
          title=""
          attribute="projectTeamRoleIds"
          facetOrdering={false}
          limit={50}
          transformItems={makeTransformItems('projectTeamRoleIds')}
          classes={classes}
          plain
        />
      </ExpansionPanelDetails>
    </ExpansionPanel>
  )
}

function ShootDetailsAccordion({
  classes,
  makeTransformItems,
}: {
  classes: ReturnType<typeof useStyles>
  makeTransformItems: (attribute: string) => (items: any[]) => any[]
}) {
  const lookup = useProjectsFacetLabelLookup()
  const filterDraft = useContext(ProjectsFilterDraftContext)
  const combined = useMemo(() => {
    if (!filterDraft) return 'Any'
    const cities = filterDraft.draftRefinementList.facetShootCityIds || []
    const types = filterDraft.draftRefinementList.facetShootTypeIds || []
    const parts = [
      ...cities.map((id) => resolveFacetLabel('facetShootCityIds', id, lookup)),
      ...types.map((id) => resolveFacetLabel('facetShootTypeIds', id, lookup)),
    ].filter(Boolean)
    return parts.length ? parts.join(', ') : 'Any'
  }, [filterDraft?.draftRefinementList, lookup])

  return (
    <ExpansionPanel
      className={classes.accRoot}
      square
      elevation={0}
      defaultExpanded={false}
      TransitionProps={{ timeout: 'auto', unmountOnExit: false } as any}
    >
      <ExpansionPanelSummary
        className={classes.accSummary}
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        aria-controls="facet-shoot-details-content"
        id="facet-shoot-details-header"
      >
        <div className={classes.accSummaryInner}>
          <Typography component="span" className={classes.accTitle}>
            Shoot details
          </Typography>
          <Typography component="span" className={classes.accSelection} title={combined || undefined}>
            {combined}
          </Typography>
        </div>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.accDetails} id="facet-shoot-details-content">
        <Typography component="div" className={classes.shootSubLabel}>
          CITY
        </Typography>
        <FacetAccordionSection
          panelId="facet-shoot-city"
          title=""
          attribute="facetShootCityIds"
          facetOrdering={false}
          searchable={false}
          facetSearchEnabled
          limit={80}
          transformItems={makeTransformItems('facetShootCityIds')}
          classes={classes}
          facetSearchPlaceholder="Search cities..."
          plain
        />
        <Typography component="div" className={classes.shootSubLabel}>
          TYPE
        </Typography>
        <FacetAccordionSection
          panelId="facet-shoot-type"
          title=""
          attribute="facetShootTypeIds"
          facetOrdering={false}
          limit={20}
          transformItems={makeTransformItems('facetShootTypeIds')}
          classes={classes}
          plain
        />
      </ExpansionPanelDetails>
    </ExpansionPanel>
  )
}

/**
 * Toolbar, facet chips, and filter drawer — sibling to hits under InstantSearch.
 */
export default function ProjectsSearchChrome() {
  return <ProjectsSearchChromeInner />
}

function ProjectsSearchChromeInner() {
  const classes = useStyles()
  const lookup = useProjectsFacetLabelLookup()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [facetsReady, setFacetsReady] = useState(false)

  const makeTransformItems = useCallback(
    (attribute: string) => (items: any[]) => transformIdFacetItems(attribute, items, lookup),
    [lookup]
  )

  // Wait for label lookup before mounting drawer facets so ids resolve to names on first paint.
  useEffect(() => {
    if (!drawerOpen) return
    if (!lookup.ready) return
    if (facetsReady) return
    const t = window.setTimeout(() => setFacetsReady(true), 0)
    return () => clearTimeout(t)
  }, [drawerOpen, facetsReady, lookup.ready])

  return (
    <ProjectsFilterDraftProvider drawerOpen={drawerOpen} onCloseDrawer={() => setDrawerOpen(false)}>
      <div className={classes.toolbarInset}>
        <div className={classes.controlsRow}>
          <RefinementList
            attribute="projectStatusId"
            facetOrdering={false}
            limit={10}
            className={classes.toolbarRefinementList}
            transformItems={(items: any[]) =>
              makeTransformItems('projectStatusId')(items).filter((item) =>
                ['Active', 'Completed'].includes(String(item.label))
              )
            }
          />
        </div>
        <ProjectsFilterErrorBoundary>
          <ProjectsRefinementChipsBridge onOpenDrawer={() => setDrawerOpen(true)} />
        </ProjectsFilterErrorBoundary>
      </div>

      <ProjectsFilterSlidePanel
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          titleId="projects-filter-drawer-title"
          footer={<ProjectsFilterDrawerFooter facetsReady={facetsReady} />}
        >
          {!facetsReady || !lookup.ready ? (
            <div className={classes.panelLoading}>
              <CircularProgress size={32} />
            </div>
          ) : (
            <ProjectsFilterErrorBoundary>
              <div className={classes.drawerContent} key={`facet-drawer-${lookup.ready}`}>
                <ProjectsHiddenBudgetRange attribute={BUDGET_RANGE_ATTRIBUTE} />
                <FacetAccordionSection
                  panelId="facet-agency"
                  title="Agency"
                  attribute="agencyId"
                  facetOrdering={false}
                  searchable={false}
                  facetSearchEnabled
                  limit={100}
                  transformItems={makeTransformItems('agencyId')}
                  classes={classes}
                  facetSearchPlaceholder="Search agencies"
                />
                <FacetAccordionSection
                  panelId="facet-region"
                  title="Region"
                  attribute="projectRegionId"
                  facetOrdering={false}
                  limit={50}
                  transformItems={makeTransformItems('projectRegionId')}
                  classes={classes}
                />
                <FacetAccordionSection
                  panelId="facet-category"
                  title="Project category"
                  attribute="projectCategoryId"
                  facetOrdering={false}
                  limit={50}
                  transformItems={makeTransformItems('projectCategoryId')}
                  classes={classes}
                />
                <FacetAccordionSection
                  panelId="facet-project-type"
                  title="Project type"
                  attribute="projectTypeId"
                  facetOrdering={false}
                  limit={50}
                  transformItems={makeTransformItems('projectTypeId')}
                  classes={classes}
                />
                <EstimatedBudgetAccordion />
                <ProjectTeamAccordion classes={classes} makeTransformItems={makeTransformItems} />
                <ShootDetailsAccordion classes={classes} makeTransformItems={makeTransformItems} />
              </div>
            </ProjectsFilterErrorBoundary>
          )}
        </ProjectsFilterSlidePanel>
    </ProjectsFilterDraftProvider>
  )
}
